import { useState, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { queryStrings } from '@trqk/resolvers';
import { getWebpackEnvVar } from '@trqk/utils';

import { useTokenModal } from './useTokenModal';

const {
    GET_STATEMENTS_UI_STATE,
    GET_PRO_INGESTION,
    GET_TRQK_STATEMENT_VIEW_AUTHENTICATED,
} = queryStrings;

/**
 * A React hook that will return an object with two properties:
 *  `dashboard`: the current PRO ingestion dashboard data for this user, or null
 *  `dashboardIsUnavailable`: boolean; true when all sources for the dashboard data have been
 *      queried and no dashboard is available
 *
 * The dashboard will be sourced following this series of steps:
 *
 * 1. Get the statementId from the cache
 *    a. If the statementId is unavailable, the dashboard is unavailable.
 * 2. Query the cache for the PRO ingestion dashboard using the statementId.
 *    a. If the dashboard is not in the cache and we have an token, use the token and
 *       statementId to get the previously ingested dashboard from the proStatementView URL.
 *    b. If the dashboard is not in the cache and we do not have an token, the dashboard is
 *       unavailable.
 * 3. If the dashboard is not returned from the proStatementView URL, the dashboard is unavailable.
 */
export const useDashboard = (): {
    dashboard: ProIngestionDashboard | null;
    dashboardIsUnavailable: boolean;
} => {
    const proStatementViewVersion = getWebpackEnvVar(
        'web',
        'PRO_STATEMENT_VIEW_VERSION',
    );
    const [dashboard, setDashboard] = useState<ProIngestionDashboard>(
        {} as ProIngestionDashboard,
    );
    const [dashboardIsUnavailable, setDashboardIsUnavailable] = useState(false);
    const [proIngestionQueryFinished, setProIngestionQueryFinished] =
        useState(false);
    const uiStateQuery = useQuery(GET_STATEMENTS_UI_STATE);
    const statementId = uiStateQuery?.data?.uiState?.statements?.statementId;
    const { getToken } = useTokenModal();

    const [getTrqkStatementViewAuthenticated] = useLazyQuery(
        GET_TRQK_STATEMENT_VIEW_AUTHENTICATED,
        {
            onCompleted: data => {
                const previouslyIngestedDashboard = _get(
                    data,
                    ['getTrqkStatementView', 'view'],
                    {},
                );

                if (_isEmpty(previouslyIngestedDashboard)) {
                    // 3. If the dashboard is not returned from the proStatementView URL, the
                    // dashboard is unavailable.
                    setDashboardIsUnavailable(true);
                } else {
                    setDashboard(previouslyIngestedDashboard);
                }
            },
            onError: (): void => {
                // 3. If the dashboard is not returned from the proStatementView URL, the dashboard
                // is unavailable.
                setDashboardIsUnavailable(true);
            },
        },
    );

    const [getProIngestion, getProIngestionQuery] = useLazyQuery(
        GET_PRO_INGESTION,
        {
            onCompleted: data => {
                const ingestedDashboard = _get(
                    data,
                    ['proIngestion', 'data', 'dashboard'],
                    {},
                );

                setDashboard(ingestedDashboard);
                setProIngestionQueryFinished(true);
            },
            onError: (): void => {
                // If there was some error with retrieving the previously ingested statement from
                // the cache, the dashboard is unavailable. This can happen if something has
                // changed unexpectedly in the return value from the API at PRO_STATEMENT_VIEW_URL.
                setDashboardIsUnavailable(true);
            },
        },
    );

    useEffect(() => {
        if (proIngestionQueryFinished && _isEmpty(dashboard)) {
            const handleToken = (): void => {
                // 2a. If the dashboard is not in the cache and we have a token, use the
                // statementId to get the previously ingested dashboard from the trqkDirectory
                // GraphQL API.

                getTrqkStatementViewAuthenticated({
                    variables: {
                        id: statementId,
                        version: proStatementViewVersion,
                    },
                });
            };

            const handleTokenMissing = (): void => {
                // 2b. If the dashboard is not in the cache and we do not have a token, the
                // dashboard is unavailable.
                setDashboardIsUnavailable(true);
            };

            getToken(handleToken, handleTokenMissing);
        }
    }, [
        dashboard,
        getToken,
        getTrqkStatementViewAuthenticated,
        statementId,
        proIngestionQueryFinished,
    ]);

    useEffect(() => {
        if (uiStateQuery.called && !uiStateQuery.loading && !statementId) {
            // 1a. If the statementId is not available, the dashboard is unavailable.
            setDashboardIsUnavailable(true);
        } else if (
            // 1. If we have a statementId and haven't called getProIngestionQuery, call
            // it to fetch the proIngestion from the cache.
            statementId &&
            !getProIngestionQuery.called
        ) {
            getProIngestion({
                variables: {
                    statementId,
                },
            });
        }
    }, [getProIngestion, getProIngestionQuery, statementId, uiStateQuery]);

    return {
        dashboard: _isEmpty(dashboard) ? null : dashboard,
        dashboardIsUnavailable,
    };
};
